import { useEffect, useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { paymentApi } from '../api/client'
import { useAuth } from '../contexts/AuthContext'
import storage from '../utils/storage'

const AI_PLANS = [
  {
    id: 'ai_10',
    name: 'AI 生图 10 次',
    price: 5.0,
    quota: 10,
    period: '30天有效',
    desc: '按次购买，灵活使用',
    icon: '🌱',
    color: 'mint',
  },
  {
    id: 'ai_weekly',
    name: 'AI 生图周卡',
    price: 6.9,
    quota: 10,
    period: '7天有效',
    desc: '低门槛体验',
    icon: '🌿',
    color: 'mint',
  },
  {
    id: 'ai_monthly',
    name: 'AI 生图月度',
    price: 29.0,
    quota: 40,
    period: '30天有效',
    desc: '性价比之选 ⭐',
    icon: '🔥',
    color: 'peach',
    popular: true,
  },
  {
    id: 'ai_yearly',
    name: 'AI 生图年度',
    price: 69.0,
    quota: 120,
    period: '365天有效',
    desc: '全年畅用',
    icon: '👑',
    color: 'peach',
  },
]

const PREMIUM_PLANS = [
  {
    id: 'premium_weekly',
    name: '会员权益周卡',
    price: 9.9,
    period: '7天',
    features: ['大画板 150×150', '无水印导出', '高清 PNG / PDF', '导出无次数限制'],
    desc: '短期体验全部权益',
    icon: '🎫',
    color: 'lavender',
  },
  {
    id: 'premium_permanent',
    name: '会员权益永久',
    price: 19.9,
    period: '永久',
    features: ['大画板 150×150', '无水印导出', '高清 PNG / PDF', '导出无次数限制'],
    desc: '一次购买，永久享用',
    icon: '💎',
    color: 'peach',
    popular: true,
  },
]

const COLOR_MAP = {
  mint: {
    border: 'border-mint/60',
    bg: 'bg-mint-light',
  },
  peach: {
    border: 'border-peach/60',
    bg: 'bg-peach-light',
  },
  lavender: {
    border: 'border-lavender/60',
    bg: 'bg-lavender/20',
  },
}

export default function Pricing() {
  const { user, isPremium } = useAuth()
  const [loading, setLoading] = useState(null)
  const [order, setOrder] = useState(null)
  const [status, setStatus] = useState(null)

  useEffect(() => {
    if (!order?.orderId) return
    const interval = setInterval(async () => {
      try {
        const { data } = await paymentApi.getOrder(order.orderId)
        const current = data.order
        if (current) {
          setStatus(current.status)
          if (current.status === 'paid' || current.status === 'success') {
            clearInterval(interval)
            alert('支付成功！权益已到账，请刷新页面。')
          }
        }
      } catch {
        // 忽略轮询错误
      }
    }, 3000)
    return () => clearInterval(interval)
  }, [order])

  const buy = async (productId) => {
    if (!storage.get('pindou_token')) {
      alert('请先登录')
      window.location.href = '/login'
      return
    }
    setLoading(productId)
    try {
      const { data } = await paymentApi.createOrder(productId)
      setOrder({
        orderId: data.orderId,
        amount: data.amount,
        payUrl: data.payUrl,
        qrCode: data.qrCode,
      })
      setStatus('pending')
    } catch (err) {
      alert(err.response?.data?.error || err.message || '创建订单失败')
    } finally {
      setLoading(null)
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-10">
      {/* 头部 */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-cocoa">购买会员权益 & AI 额度 🎨</h1>
        <p className="mt-2 text-cocoa-light">
          会员权益与 AI 生图额度完全分离，按需购买
        </p>
        {isPremium && (
          <p className="mt-2 text-sm text-mint-dark">
            🌾 你已经是田主会员，享受无限制导出等基础权益
          </p>
        )}
      </div>

      {/* 当前额度 */}
      {user && (
        <div className="card mx-auto max-w-md text-center">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-cocoa-light">AI 生图额度</p>
              <p className="mt-1 text-2xl font-extrabold text-cocoa">{user.quota || 0}</p>
              {user.quotaExpiresAt > 0 && (
                <p className="mt-0.5 text-xs text-cocoa-light">
                  有效期至 {new Date(user.quotaExpiresAt).toLocaleDateString('zh-CN')}
                </p>
              )}
            </div>
            <div>
              <p className="text-sm text-cocoa-light">会员状态</p>
              <p className="mt-1 text-2xl font-extrabold text-cocoa">
                {isPremium ? '已开通' : '未开通'}
              </p>
              {user.premiumUntil > 0 && user.premiumUntil < 9999999999999 && (
                <p className="mt-0.5 text-xs text-cocoa-light">
                  到期 {new Date(user.premiumUntil).toLocaleDateString('zh-CN')}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 会员权益板块 */}
      <div>
        <div className="mb-4 flex items-center gap-2">
          <span className="text-2xl">🌾</span>
          <h2 className="text-xl font-bold text-cocoa">会员权益</h2>
          <span className="rounded-full bg-cream-200 px-2.5 py-0.5 text-xs text-cocoa-muted">不含 AI 生图</span>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {PREMIUM_PLANS.map((plan) => {
            const c = COLOR_MAP[plan.color]
            return (
              <div
                key={plan.id}
                className={`relative overflow-hidden rounded-[2rem] border p-6 shadow-card transition hover:-translate-y-1 ${c.border} ${c.bg}`}
              >
                {plan.popular && (
                  <span className="absolute right-[-2.5rem] top-4 w-28 rotate-45 bg-blush py-1 text-center text-xs font-bold text-white shadow-sm">
                    超值推荐
                  </span>
                )}
                <div className="text-4xl">{plan.icon}</div>
                <h3 className="relative mt-3 text-xl font-bold text-cocoa">{plan.name}</h3>
                <p className="relative text-sm text-cocoa-light">{plan.desc}</p>
                <div className="relative my-4 flex items-baseline gap-1">
                  <span className="text-4xl font-extrabold text-cocoa">¥{plan.price}</span>
                  <span className="text-cocoa-light">/ {plan.period}</span>
                </div>
                <ul className="relative mb-4 space-y-2">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-cocoa-light">
                      <span className="text-blush-dark">✓</span> {f}
                    </li>
                  ))}
                </ul>
                <p className="relative mb-4 text-xs text-red-400">
                  ⚠️ 不包含 AI 生图功能，需另外购买额度包
                </p>
                <button
                  onClick={() => buy(plan.id)}
                  disabled={loading === plan.id}
                  className="relative w-full rounded-full bg-blush py-2.5 font-bold text-white shadow-soft transition hover:bg-blush-dark disabled:opacity-60"
                >
                  {loading === plan.id ? '创建订单…' : '立即购买'}
                </button>
              </div>
            )
          })}
        </div>
      </div>

      {/* AI 生图额度板块 */}
      <div>
        <div className="mb-4 flex items-center gap-2">
          <span className="text-2xl">🎨</span>
          <h2 className="text-xl font-bold text-cocoa">AI 生图额度</h2>
          <span className="rounded-full bg-cream-200 px-2.5 py-0.5 text-xs text-cocoa-muted">含会员权益</span>
        </div>
        <p className="mb-4 text-sm text-cocoa-light">
          💡 <b>月度和年度 AI 额度包自动解锁永久会员权益</b>（大画板、无水印导出等），仅额度数量与时限不同
        </p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {AI_PLANS.map((plan) => {
            const c = COLOR_MAP[plan.color]
            const unlocksPremium = plan.id === 'ai_monthly' || plan.id === 'ai_yearly'
            return (
              <div
                key={plan.id}
                className={`relative overflow-hidden rounded-[2rem] border p-6 shadow-card transition hover:-translate-y-1 ${c.border} ${c.bg}`}
              >
                {plan.popular && (
                  <span className="absolute right-[-2.5rem] top-4 w-28 rotate-45 bg-blush py-1 text-center text-xs font-bold text-white shadow-sm">
                    超值推荐
                  </span>
                )}
                {unlocksPremium && (
                  <span className="absolute left-3 top-3 rounded-full bg-mint px-2 py-0.5 text-[10px] font-bold text-cocoa">
                    送永久会员
                  </span>
                )}
                <div className="text-4xl">{plan.icon}</div>
                <h3 className="relative mt-3 text-lg font-bold text-cocoa">{plan.name}</h3>
                <p className="relative text-sm text-cocoa-light">{plan.desc}</p>
                <div className="relative my-4 flex items-baseline gap-1">
                  <span className="text-3xl font-extrabold text-cocoa">¥{plan.price}</span>
                </div>
                <div className="relative mb-4 rounded-2xl bg-white/60 p-3 text-center">
                  <span className="text-2xl font-bold text-cocoa">{plan.quota}</span>
                  <span className="text-sm text-cocoa-light"> 次</span>
                  <p className="text-xs text-cocoa-light">{plan.period}</p>
                </div>
                {unlocksPremium && (
                  <p className="relative mb-3 text-xs text-mint-dark">
                    ✓ 包含永久会员权益（大画板/无水印导出等）
                  </p>
                )}
                <button
                  onClick={() => buy(plan.id)}
                  disabled={loading === plan.id}
                  className="relative w-full rounded-full bg-blush py-2.5 font-bold text-white shadow-soft transition hover:bg-blush-dark disabled:opacity-60"
                >
                  {loading === plan.id ? '创建订单…' : '立即购买'}
                </button>
              </div>
            )
          })}
        </div>
      </div>

      {/* 底部说明 */}
      <div className="rounded-[2rem] border border-lavender/40 bg-cream-50 p-6 text-sm text-cocoa-light">
        <h3 className="mb-2 font-bold text-cocoa">💡 购买须知</h3>
        <ul className="space-y-1.5">
          <li>· <b>会员权益</b>：包含大画板、无水印导出、高清 PDF 等基础功能，<span className="text-red-400">不含 AI 生图</span></li>
          <li>· <b>AI 生图月度/年度包</b>：购买即<span className="text-mint-dark">自动解锁永久会员权益</span>，无需再购买会员</li>
          <li>· <b>AI 生图 10次/周卡</b>：仅含 AI 额度，不含会员权益，需单独购买会员或已有会员时长</li>
          <li>· 按次购买的额度 30 天有效，订阅包在订阅期内有效</li>
          <li>· 额度用完或过期后，仍可正常使用非 AI 功能</li>
          <li>· 每日签到可获得额外额度奖励</li>
        </ul>
      </div>

      {/* 支付弹层 */}
      {order && (
        <div className="card mx-auto max-w-md text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-blush-light text-2xl">🧾</div>
          <h3 className="text-lg font-bold text-cocoa">订单已创建</h3>
          <p className="mt-1 text-2xl font-bold text-blush-dark">¥{order.amount}</p>

          {order.payUrl && (
            <div className="mt-4 space-y-3">
              <button
                onClick={() => {
                  window.open(order.payUrl, '_blank')
                }}
                className="w-full rounded-full bg-[#1677FF] py-2.5 font-bold text-white shadow-md transition hover:bg-[#1366CC]"
              >
                打开支付宝支付
              </button>
              <p className="text-xs text-cocoa-light">
                点击后会跳转支付宝；若未自动打开，请用支付宝扫下方二维码
              </p>
            </div>
          )}

          <div className="my-4 flex justify-center rounded-2xl bg-white p-3">
            {order.payUrl ? (
              <QRCodeSVG value={order.payUrl} size={180} bgColor="transparent" fgColor="currentColor" className="text-cocoa" />
            ) : (
              <div className="text-sm text-cocoa-light">暂无支付链接</div>
            )}
          </div>

          <p className="text-sm text-cocoa-light">
            订单状态：<span className="font-medium text-blush-dark">{status || 'pending'}</span>
          </p>
        </div>
      )}
    </div>
  )
}
